import { client, invokeWithTimeout } from './client';

import {
    Membership,
    MembershipType,
    CreateMembership,
    EndMembership,
    UpdateMembershipType,
    FullMembership,
    UpdateMembershipStart,
    ApproveMembership,
} from '../../models/Membership';
import { ApproveVolunteeringRecord, CreateVolunteeringRecord, CreateVolunteeringRecordDto, VolunteeringRecord, VolunteeringRecordDto } from '../../models/Volunteering';
import { toLocalISODateString } from '../../utils/time';
import { logWarning } from '../../utils/logger';
import { throwIfSupabaseError } from './throwIfSupabaseError';

const table = 'memberships';

export async function getMembership(): Promise<FullMembership | null> {
    const response = await invokeWithTimeout<FullMembershipDto>('get-full-memberships?own=true');
    throwIfSupabaseError(response);

    if (response.data) {
        return transformMembershipDtoToFullMembership(response.data);
    }

    return null;
}

export async function getMembershipList(): Promise<FullMembership[]> {
    const response = await invokeWithTimeout<FullMembershipDto[]>('get-full-memberships');
    throwIfSupabaseError(response);

    if (response.data == null) {
        logWarning('No membership data received from get-full-memberships function');
        return [];
    }

    return response.data.map((membership) => transformMembershipDtoToFullMembership(membership));
}

export async function getMembershipForUser(userId: string): Promise<FullMembership | null> {
    const response = await invokeWithTimeout<FullMembershipDto>(`get-full-memberships?user_id=${userId}`);
    throwIfSupabaseError(response);

    if (response.data == null) {
        return null;
    }

    return transformMembershipDtoToFullMembership(response.data);
}

function transformMembershipDtoToFullMembership(membership: FullMembershipDto): FullMembership {
    return {
        id: membership.id,
        userId: membership.user_id,
        firstName: membership.first_name,
        lastName: membership.last_name,
        oib: membership.oib,
        dateOfBirth: new Date(membership.date_of_birth),
        startsAt: new Date(membership.starts_at),
        endsAt: membership.ends_at ? new Date(membership.ends_at) : undefined,
        membershipType: membership.membership_type as MembershipType,
        email: membership?.email ?? '',
        username: membership?.username ?? '',
        discordUsername: membership?.discord_username ?? '',
        approvedAt: membership.approved_at ? new Date(membership.approved_at) : undefined,
        hoursVolunteered: membership.hours_volunteered ?? 0,
        gamesWithoutRecord: (membership.games_without_record ?? []).map(g => ({
            gameId: g.game_id,
            title: g.title,
            date: new Date(g.date),
        })),
    };
}

export async function createMembership(dto: CreateMembership): Promise<FullMembership | null> {
    const response = await client
        .from(table)
        .insert({
            user_id: dto.userId,
            first_name: dto.firstName,
            last_name: dto.lastName,
            oib: dto.oib,
            date_of_birth: toLocalISODateString(dto.dateOfBirth),
            starts_at: new Date().toISOString(),
            membership_type: MembershipType.Supporting,
        });
    throwIfSupabaseError(response);

    return getMembership();
}

export async function updateMembershipStart(
    dto: UpdateMembershipStart,
): Promise<void> {
    const response = await client
        .from(table)
        .update({
            starts_at: dto.startsAt.toISOString(),
        })
        .eq('user_id', dto.userId);
    throwIfSupabaseError(response);
}

export async function approveMembership(
    dto: ApproveMembership,
): Promise<void> {
    const response = await client
        .from(table)
        .update({
            approved_at: new Date().toISOString(),
        })
        .eq('user_id', dto.userId);
    throwIfSupabaseError(response);
}

export async function endMembership(
    dto: EndMembership
): Promise<void> {
    const response = await client
        .from(table)
        .update({
            ends_at: dto.endsAt.toISOString(),
        })
        .eq('user_id', dto.userId);
    throwIfSupabaseError(response);
}

export async function updateMembershipType(
    userId: Membership['userId'],
    dto: UpdateMembershipType
): Promise<void> {
    const response = await client
        .from(table)
        .update({
            membership_type: dto.membershipType,
        })
        .eq('user_id', userId);
    throwIfSupabaseError(response);
}

export async function insertVolunteeringRecord(payload: CreateVolunteeringRecord): Promise<VolunteeringRecord> {
    const dto: CreateVolunteeringRecordDto = {
        date: payload.date.toISOString(),
        hours: payload.hours,
        description: payload.description,
        member_id: payload.memberId,
        game_id: payload.gameId,
    };

    const response = await client
        .from('volunteering_records')
        .insert(dto)
        .select()
        .single();
    throwIfSupabaseError(response);

    if (!response.data) {
        throw new Error('No data returned when inserting volunteering record');
    }

    return transformVolunteeringRecord(response.data);
}

export async function approveVolunteeringRecord(payload: ApproveVolunteeringRecord): Promise<VolunteeringRecord> {
    const dto: ApproveVolunteeringRecord = {
        id: payload.id,
        approvedById: payload.approvedById,
    };

    const response = await client
        .from('volunteering_records')
        .update({
            approved_at: new Date().toISOString(),
            approved_by: dto.approvedById,
        })
        .eq('id', dto.id)
        .select()
        .single();
    throwIfSupabaseError(response);

    if (!response.data) {
        throw new Error('No data returned when approving volunteering record');
    }

    return transformVolunteeringRecord(response.data);
}

export async function deleteVolunteeringRecord(id: string): Promise<void> {
    const response = await client
        .from('volunteering_records')
        .delete()
        .eq('id', id);
    throwIfSupabaseError(response);
}

function transformVolunteeringRecord(dto: VolunteeringRecordDto): VolunteeringRecord {
    return {
        id: dto.id,
        createdAt: new Date(dto.created_at),
        date: new Date(dto.date),
        hours: dto.hours,
        description: dto.description ?? undefined,
        memberId: dto.member_id,
        approvedAt: dto.approved_at ? new Date(dto.approved_at) : undefined,
        approvedById: dto.approved_by ?? undefined,
        isApproved: dto.approved_at != null,
        gameId: dto.game_id,
    };
}

type FullMembershipDto = {
    id: string;
    user_id: string;
    first_name: string;
    last_name: string;
    oib: string;
    date_of_birth: string;
    starts_at: string;
    ends_at: string | null;
    membership_type: string;
    approved_at: string | null;
    username?: string;
    discord_username?: string;
    email?: string;
    hours_volunteered?: number;
    games_without_record?: {
        game_id: string;
        title: string;
        date: string;
    }[];
};
